import { get, post, put, upload } from "@/api";

export interface PurchaseCategorySummary {
  category_id?: number | string;
  category_name?: string;
  plan_quantity?: number;
  plan_amount?: number | string;
}

export interface PurchasePlanRecord {
  id: number | string;
  apply_month?: string | number;
  apply_month_text?: string;
  category_id?: number | string;
  category_name?: string;
  status?: number | string;
  status_text?: string;
  account_id?: number | string;
  created_at?: string;
  limit_time_start?: string;
  limit_time_end?: string;
  plan_quantity?: number;
  plan_amount?: number | string;
  category_summary?: PurchaseCategorySummary[];
  office?: { id?: number | string; title?: string } | null;
  member?: { id?: number | string; nickname?: string; account?: string } | null;
  instan?: Array<{
    id?: number | string;
    sort?: number | string;
    step_name?: string;
    step?: string;
    account_ids?: string | null;
    account_name?: string | null;
    account_names?: string | null;
    approve_state?: number | string;
    approve?: string | null;
    approve_time?: string | null;
    now_step_sort?: number | string;
    is_over?: number | string;
  }>;
}

export interface PurchasePlanPagesParams {
  page?: number;
  pagenumber?: number;
  year?: number | string;
  month?: string;
  category_id?: number | string;
  status?: number | number[];
  /** 科室筛选（is_divide 账号） */
  department_ids?: Array<number | string>;
  office_ids?: Array<number | string>;
  /** 我的待办（待我审批的采购计划） */
  is_todo?: boolean;
}

export interface PurchasePlanPagesResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: PurchasePlanRecord[];
  total?: number;
  page?: number;
  pagenumber?: number;
}

/** 科室采购计划列表 */
export function getPurchasePlanPages(params: PurchasePlanPagesParams = {}) {
  return post<PurchasePlanPagesResponse>("/platform/purchase/pages", params);
}

export interface PurchasePlanGoodsRow {
  goods_id?: number | string;
  spec_id?: number | string;
  goods_name?: string | null;
  goods_pic?: string | null;
  spec_name?: string | null;
  code?: string | null;
  unit?: string | null;
  plan_quantity?: number | string;
  used_quantity?: number | string;
  return_quantity?: number | string;
  remain_quantity?: number | string;
  price?: number | string | null;
  plan_amount?: number | string;
  used_amount?: number | string;
  return_amount?: number | string;
  remain_amount?: number | string;
  /** 已使用比例（百分比） */
  usage_ratio?: number | string;
  /** @deprecated 兼容旧字段，等同净用量 used - return */
  sale_quantity?: number | string;
  /** @deprecated 兼容旧字段，等同 remain_quantity */
  limit_quantity?: number | string;
  sale_amount?: number | string;
  goods?: { id?: number | string; title?: string; m_pic?: string } | null;
  spec?: {
    id?: number | string;
    name?: string;
    unit?: string;
    sku?: string;
  } | null;
}

export interface PurchasePlanGoodsResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: {
    flow?: PurchasePlanRecord;
    goods?: PurchasePlanGoodsRow[];
  };
}

/** 采购计划商品明细 */
export function getPurchasePlanGoods(flowId: number | string) {
  return post<PurchasePlanGoodsResponse>("/platform/purchase/goods", {
    flow_id: flowId,
  });
}

export interface PurchaseTemplateResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  url?: string;
  sheet_count?: number;
}

/** 下载采购计划 Excel 模板 */
export function downloadPurchaseTemplate() {
  return get<PurchaseTemplateResponse>("/platform/purchase/template");
}

export interface PurchaseSubmitParams {
  /** YYYY-MM 或 Ym */
  month: string;
  file: File;
  /** 目标科室：is_divide 账号必填 */
  department_id?: number | string;
  /** 可选，限定导入分类范围 */
  category_id?: number | string;
  remark?: string;
  /** 修改已有审批中计划时传入 */
  flow_id?: number | string;
  /** append 增量同步；overwrite 整表覆盖（修改时默认 overwrite） */
  mode?: "append" | "overwrite";
}

export interface PurchaseSubmitResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
  data?: {
    flow_id?: number | string;
    apply_month?: string;
    category_id?: number | string;
    goods_count?: number;
    mode?: string;
  };
}

/** 提交采购计划（multipart 上传；须 POST，PHP PUT 不解析 $_FILES） */
export function submitPurchasePlan(params: PurchaseSubmitParams) {
  const form = new FormData();
  form.append("month", params.month);
  form.append("file", params.file);
  if (params.department_id != null && params.department_id !== "") {
    form.append("department_id", String(params.department_id));
  }
  if (params.category_id != null && params.category_id !== "") {
    form.append("category_id", String(params.category_id));
  }
  if (params.remark?.trim()) {
    form.append("remark", params.remark.trim());
  }
  if (params.flow_id != null && params.flow_id !== "") {
    form.append("flow_id", String(params.flow_id));
  }
  if (params.mode) {
    form.append("mode", params.mode);
  }
  return upload<PurchaseSubmitResponse>("/platform/purchase/submit", form);
}

export interface PurchaseApproveParams {
  flow_id: number | string;
  /** true 同意，false 拒绝 */
  is_agree: boolean;
  remark?: string;
}

export interface PurchaseApproveResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
}

/** 采购计划审批 */
export function approvePurchasePlan(params: PurchaseApproveParams) {
  return put<PurchaseApproveResponse>("/platform/purchase/approve", params);
}

export interface PurchaseCancelParams {
  flow_id: number | string;
  remark: string;
}

export interface PurchaseCancelResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
}

/** 取消采购计划（未审批 / 审批中） */
export function cancelPurchasePlan(params: PurchaseCancelParams) {
  return put<PurchaseCancelResponse>("/platform/purchase/cancel", params);
}

export interface PurchaseUnapproveParams {
  flow_id: number | string;
  remark?: string;
}

export interface PurchaseUnapproveResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
  data?: {
    flow_id?: number | string;
    status?: number;
    status_text?: string;
  };
}

/** 采购计划反审（仅已通过） */
export function unapprovePurchasePlan(params: PurchaseUnapproveParams) {
  return put<PurchaseUnapproveResponse>("/platform/purchase/unapprove", params);
}
